/**
 * FloatingButton component for the chatbot widget.
 * Renders a fixed-position circular FAB that toggles the chat panel.
 */

const CHAT_ICON_SVG = `<svg class="chatbot-fab__icon chatbot-fab__icon--chat" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>`;

const CLOSE_ICON_SVG = `<svg class="chatbot-fab__icon chatbot-fab__icon--close" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" hidden><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>`;

export class FloatingButton {
  private button: HTMLButtonElement;
  private container: HTMLElement;
  private onToggle: () => void;
  private handleClick: () => void;

  constructor(container: HTMLElement, onToggle: () => void) {
    this.container = container;
    this.onToggle = onToggle;

    this.button = document.createElement('button');
    this.button.className = 'chatbot-fab';
    this.button.setAttribute('aria-label', 'Open chat');
    this.button.setAttribute('type', 'button');

    // Inline styles for positioning — CSS file will override later
    Object.assign(this.button.style, {
      position: 'fixed',
      bottom: '16px',
      right: '16px',
      zIndex: '9999',
      width: '56px',
      height: '56px',
      minWidth: '44px',
      minHeight: '44px',
      border: 'none',
      borderRadius: '50%',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0',
    });

    this.button.innerHTML = CHAT_ICON_SVG + CLOSE_ICON_SVG;

    this.handleClick = () => {
      this.onToggle();
    };

    this.button.addEventListener('click', this.handleClick);
    this.container.appendChild(this.button);
  }

  /**
   * Updates the button state to reflect whether the panel is open or closed.
   * Swaps icon visibility and updates the aria-label.
   */
  setState(panelOpen: boolean): void {
    const chatIcon = this.button.querySelector(
      '.chatbot-fab__icon--chat',
    ) as SVGElement | null;
    const closeIcon = this.button.querySelector(
      '.chatbot-fab__icon--close',
    ) as SVGElement | null;

    if (panelOpen) {
      chatIcon?.setAttribute('hidden', '');
      closeIcon?.removeAttribute('hidden');
      this.button.setAttribute('aria-label', 'Close chat');
    } else {
      chatIcon?.removeAttribute('hidden');
      closeIcon?.setAttribute('hidden', '');
      this.button.setAttribute('aria-label', 'Open chat');
    }
  }

  /**
   * Returns the button HTMLElement.
   */
  getElement(): HTMLButtonElement {
    return this.button;
  }

  /**
   * Removes the button from the DOM and cleans up event listeners.
   */
  destroy(): void {
    this.button.removeEventListener('click', this.handleClick);
    this.button.remove();
  }
}
